import { Link } from "react-router-dom";
import { ArrowRight, Info, HardHat, Check, Plus } from "lucide-react";
import { AID_NOTICE } from "../../data/site";
import { Reveal } from "./Reveal";

export const PageHero = ({ eyebrow, title, intro, image, imageAlt, children, testid = "page-hero" }) => (
  <section data-testid={testid} className="roof-bg overflow-hidden">
    <div className="container-x grid lg:grid-cols-12 gap-10 pt-12 pb-14 md:pt-16 md:pb-20 items-center">
      <div className={image ? "lg:col-span-6" : "lg:col-span-9"}>
        {eyebrow && <p className="eyebrow rise">{eyebrow}</p>}
        <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl leading-[1.05] rise rise-1">{title}</h1>
        {intro && <p className="mt-5 text-lg text-slate-600 font-light max-w-xl rise rise-2">{intro}</p>}
        {children && <div className="mt-8 flex flex-wrap gap-3 rise rise-3">{children}</div>}
      </div>
      {image && (
        <div className="lg:col-span-6 rise rise-2">
          <img src={image} alt={imageAlt} className="w-full aspect-[4/3] object-cover rounded-[32px] shadow-[0_40px_80px_-40px_rgba(16,36,62,0.45)]" />
        </div>
      )}
    </div>
  </section>
);

export const Section = ({ eyebrow, title, intro, children, className = "", tone = "white", id, testid }) => (
  <section id={id} data-testid={testid} className={`py-16 md:py-24 ${tone === "sand" || tone === "mist" ? "bg-mist-50" : tone === "dark" ? "bg-navy text-white" : "bg-white"} ${className}`}>
    <div className="container-x">
      {(eyebrow || title) && (
        <Reveal className="max-w-2xl mb-10 md:mb-12">
          {eyebrow && <p className={`eyebrow ${tone === "dark" ? "!text-brand-200" : ""}`}>{eyebrow}</p>}
          {title && <h2 className={`mt-3 text-3xl sm:text-4xl leading-tight ${tone === "dark" ? "!text-white" : ""}`}>{title}</h2>}
          {intro && <p className={`mt-4 text-lg font-light ${tone === "dark" ? "text-white/75" : "text-slate-600"}`}>{intro}</p>}
        </Reveal>
      )}
      {children}
    </div>
  </section>
);

export const AidNotice = ({ className = "" }) => (
  <p data-testid="aid-indicative-notice" className={`flex gap-2 items-start text-[15px] text-slate-700 bg-brand-50 rounded-2xl px-4 py-3 border border-brand/15 ${className}`}>
    <Info className="h-5 w-5 text-brand-ink shrink-0 mt-0.5" aria-hidden="true" /> {AID_NOTICE}
  </p>
);

export const ThirdPartyNotice = ({ className = "" }) => (
  <div data-testid="third-party-works-notice" className={`flex gap-4 items-start rounded-3xl border border-brand/20 bg-brand-50 p-5 ${className}`}>
    <span className="h-10 w-10 rounded-xl bg-white text-brand-ink flex items-center justify-center shrink-0"><HardHat className="h-5 w-5" aria-hidden="true" /></span>
    <p className="text-slate-700 text-[15px]"><strong className="text-navy font-semibold">Habitalys n'exécute pas les travaux.</strong> Ils sont réalisés par des entreprises tierces qualifiées et assurées ; nous conseillons, coordonnons et contrôlons.</p>
  </div>
);

export const CheckList = ({ items, dark = false }) => (
  <ul className="space-y-3">
    {items.map((it) => (
      <li key={it} className="flex gap-3 items-start">
        <span className={`mt-0.5 h-6 w-6 rounded-full flex items-center justify-center shrink-0 ${dark ? "bg-white/10 text-brand-200" : "bg-brand-100 text-brand-ink"}`}><Check className="h-4 w-4" aria-hidden="true" /></span>
        <span className={dark ? "text-white/85" : "text-slate-700"}>{it}</span>
      </li>
    ))}
  </ul>
);

export const CtaBand = ({ title = "Parlons de votre projet", text = "Réponse sous 48 h ouvrées. Un seul interlocuteur, du diagnostic à la réception." }) => (
  <section data-testid="cta-band" className="relative bg-navy overflow-hidden">
    <svg aria-hidden="true" className="absolute inset-0 h-full w-full opacity-[0.12]" viewBox="0 0 100 40" preserveAspectRatio="none">
      <path d="M0 34 L100 4 L100 9 L0 39 Z" fill="#9DB8D6" />
    </svg>
    <Reveal className="relative container-x py-16 md:py-20 grid lg:grid-cols-12 gap-8 items-center">
      <div className="lg:col-span-7">
        <h2 className="text-3xl sm:text-4xl !text-white">{title}</h2>
        <p className="mt-3 text-lg text-white/75 font-light max-w-xl">{text}</p>
      </div>
      <div className="lg:col-span-5 flex flex-wrap gap-3 lg:justify-end">
        <Link to="/contact" className="btn bg-white text-navy hover:bg-brand-100" data-testid="cta-band-quote">Demander un devis <ArrowRight className="h-5 w-5" aria-hidden="true" /></Link>
        <Link to="/prendre-rdv" className="btn-ghost-light" data-testid="cta-band-rdv">Prendre RDV</Link>
      </div>
    </Reveal>
  </section>
);

export const Faq = ({ items, prefix = "faq" }) => (
  <div className="grid gap-3">
    {items.map((f, i) => (
      <details key={f.q} className="group rounded-2xl border border-brand/15 bg-white px-5 py-4 open:bg-brand-50 transition-colors" data-testid={`${prefix}-item-${i}`}>
        <summary className="cursor-pointer list-none flex justify-between items-center gap-6 font-display text-navy text-[17px]">
          {f.q}<span className="h-8 w-8 rounded-full bg-brand-100 text-brand-ink flex items-center justify-center shrink-0 transition-transform group-open:rotate-45"><Plus className="h-4 w-4" aria-hidden="true" /></span>
        </summary>
        <p className="mt-3 text-slate-700 max-w-3xl">{f.a}</p>
      </details>
    ))}
  </div>
);
